// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useFirmwareQuery } from "@/api/hooks/use-firmware";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FirmwareActionsMenu } from "@/features/hardware/firmware-actions-menu";
import { FirmwareAssignmentsTab } from "@/features/hardware/firmware-assignments-tab";
import {
  FirmwareCpuTypeBadge,
  FirmwareDeviceTypeBadge,
  FirmwareProductionBadge,
} from "@/features/hardware/firmware-badges";
import { FirmwareInfoTab } from "@/features/hardware/firmware-info-tab";
import { FirmwareToggleDialog, type FirmwareToggleTarget } from "@/features/hardware/firmware-toggle-dialog";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/hardware/firmware/$firmwareId")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.hardwareFirmware}>
      <FirmwareDetailPage />
    </RequirePermission>
  ),
});

function FirmwareDetailPage() {
  const { firmwareId: firmwareIdParam } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const firmwareId = Number(firmwareIdParam);
  const { data: version, isLoading, isError } = useFirmwareQuery(firmwareId);

  const [toggleTarget, setToggleTarget] = useState<FirmwareToggleTarget | null>(null);
  const [toggleField, setToggleField] = useState<"active" | "production">("active");

  const activeTab = tab === "assignments" ? "assignments" : "info";
  const breadcrumbs = useMemo(() => [{ label: t("firmware.title"), to: "/hardware/firmware" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/hardware/firmware/$firmwareId",
      params: { firmwareId: firmwareIdParam },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !version) {
    return (
      <div>
        <PageHeader title={`#${firmwareIdParam}`} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={version.versionStr}
        subtitle={
          <>
            <IdPill id={version.firmwareId} />
            <FirmwareDeviceTypeBadge deviceType={version.deviceType} />
            {version.cpuType && <FirmwareCpuTypeBadge cpuType={version.cpuType} />}
            <FirmwareProductionBadge isProduction={version.isProduction} />
            <StatusIndicator active={version.isActive} />
            <FirmwareActionsMenu
              isActive={version.isActive}
              isProduction={version.isProduction}
              onToggleActive={() => {
                setToggleField("active");
                setToggleTarget(toTarget(version));
              }}
              onToggleProduction={() => {
                setToggleField("production");
                setToggleTarget(toTarget(version));
              }}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("firmware.detail.tabs.info")}</TabsTrigger>
              <TabsTrigger value="assignments">{t("firmware.detail.tabs.assignments")}</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <FirmwareInfoTab version={version} />
          </TabsContent>
          <TabsContent value="assignments" className="space-y-4">
            <FirmwareAssignmentsTab
              firmwareId={firmwareId}
              initialFilters={activeTab === "assignments" ? f : undefined}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FirmwareToggleDialog target={toggleTarget} field={toggleField} onClose={() => setToggleTarget(null)} />
    </div>
  );
}

function toTarget(v: { firmwareId: number; versionStr: string; isActive: boolean; isProduction: boolean }) {
  return {
    firmwareId: v.firmwareId,
    versionStr: v.versionStr,
    isActive: v.isActive,
    isProduction: v.isProduction,
  };
}
